/**
 * The two things `CommonBaseMixin` knew about the viewport.
 *
 * `isMobileApp` is not a screen size — it is whether this is the Capacitor build, which Quasar 1
 * reports as `$q.capacitor`. It lives here because the code that asks is almost always deciding
 * between a phone layout and a desktop one.
 *
 * `onFirstClientRender` is the other half of the mixin's `@Watch('$q.screen.width')`: on an SSR page
 * the width is 0 until the browser takes over, so the first non-zero width is the signal that
 * hydration has happened. The watcher itself stays in the mixin — a decorator cannot move into a
 * composable — and calls this to decide whether it is the first one.
 */

/**
 * `capacitor` is not on Quasar 1's `QVueGlobals` type — the mixin carried a `@ts-ignore` for it — so
 * the host is typed loosely enough to accept a component instance.
 */
export interface ScreenHost {
  $q: Record<string, any>
}

export function useScreen (host?: ScreenHost) {
  const isMobileApp = (): boolean => {
    return !!host?.$q?.capacitor
  }

  /**
   * Returns true exactly once, for the transition from "no width" to "a width".
   */
  const isFirstClientRender = (to: number, from: number, alreadyRendered: boolean): boolean => {
    return from === 0 && to > 0 && !alreadyRendered
  }

  return {
    isMobileApp,
    isFirstClientRender
  }
}
